import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  useToast,
  Flex,
  Button,
} from "@chakra-ui/react";
import { cqAPI, getBid } from "../../lib/Auth";
import CustomizeAIDetail from "./CustomizeAIDetail";
import moment from "moment/moment";
import { useNavigate } from "react-router-dom";


const deepEqual = (a, b) => {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || !a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!bKeys.includes(k)) return false;
    if (!deepEqual(a[k], b[k])) return false;
  }
  return true;
};


const CustomizeAIManualEdit = () => {
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const [updatedAt, setUpdatedAt] = useState("");
  const [bid, setBid] = useState("");
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);                  // current edits (organized_instructions)
  const [associatedBusinesses, setAssociatedBusinesses] = useState([]);
  const [disableSmsPostAppointment, setDisableSmsPostAppointment] = useState(false);
  const [initialValues, setInitialValues] = useState(null); // snapshot from DB, used for dirty check


  useEffect(() => {
    (async () => {
      try {
        const response = await cqAPI.get("/settings/ai");
        if (response.type === "ok") {
          const oi = response?.data?.organized_instructions || {};
          const disableSms = !!response?.data?.disable_sms_post_appointment;

          setForm(oi);
          setDisableSmsPostAppointment(disableSms);
          setInitialValues({ oi, disableSms });

          setUpdatedAt(response?.data?.updated || response?.data?.created || "N/A");
          setBid(response?.data?.bid || getBid());
          setAssociatedBusinesses(response?.data?.associated_businesses || []);
        }
      } catch (error) {
        console.error("Error loading AI settings:", error);
        toast({
          title: "Error",
          description: "Failed to load AI settings.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    })();
  }, []);

  const edited = useMemo(() => {
    if (!initialValues || !form) return false;
    return !deepEqual(initialValues.oi, form)
      || initialValues.disableSms !== disableSmsPostAppointment;
  }, [initialValues, form, disableSmsPostAppointment]);

  const handleUpdate = async () => {
    try {
      const res = await cqAPI.put("/settings/ai", {
        organized_instructions: form,
        disable_sms_post_appointment: disableSmsPostAppointment,
      });
      if (res.type === "ok") {
        setInitialValues({
          oi: form,
          disableSms: disableSmsPostAppointment,
        });
        toast({
          title: res.msg || "Saved successfully.",
          description: "Your AI settings have been updated.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: res.msg || "Failed to save.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({
        title: "Error",
        description: e?.msg || "Unexpected error while saving.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box my={8} px={4} width="100%" height="100%" overflowY="auto">
      <Box p={1} bgColor={bgColor} borderRadius="md" width="full">
        <Flex minH="90px" flexDirection="row" alignItems="center">
          <Flex justifyContent="flex-start" flexDirection="column" alignItems='start'>
            <Text align="start" fontSize="sm" fontWeight="bold" ml={2}>
              Ai Settings
            </Text>
            <Text fontSize="sm" ml={2} mt={1}>
              Updated: {updatedAt && moment(updatedAt).format("MMMM D, YYYY h:mm a")}
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" gap={2} ml='auto' mr={4} minHeight="20px" minWidth="20px" align="center">
            <Button variant="ghost" onClick={() => navigate(`/settings/customize-ai?bid=${bid}`)}>
              Close
            </Button>
            <Button
              isDisabled={!edited}
              onClick={async () => {
                await handleUpdate();
                navigate(`/settings/customize-ai?bid=${bid}`)
              }}
            >
              Update
            </Button>
          </Flex>
        </Flex>
      </Box>
      <Box mt={4} p={1} bgColor={bgColor} borderRadius="md" width="full">
        <Flex align="center" minH="90px" flexDirection="column" alignItems="start">
          <CustomizeAIDetail organized_instructions={form} inEditMode={true} onChange={setForm} associatedBusinesses={associatedBusinesses} disableSmsPostAppointment={disableSmsPostAppointment} onDisableSmsPostAppointmentChange={setDisableSmsPostAppointment}/>
        </Flex>
      </Box>
    </Box>
  )
};

export default CustomizeAIManualEdit;
